/**
 * Salvation Team overview (/app/salvation)
 *
 * The old app's five counters (active, still to reach, spoken to, met at church, handed over) plus what needs a
 * leader now (past the deadline, Backup responsible, needs review, not assigned), the follow-up progress bar, one row
 * per campus team and the priorities (nobody reached yet, nearest deadline first). Leaders see the campuses they lead.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  IconUsers,
  IconPhoneOff,
  IconPhone,
  IconBuildingChurch,
  IconTrendingUp,
  IconClockExclamation,
  IconArrowsExchange,
  IconEye,
  IconUserQuestion,
  IconPlus,
  IconHeart,
} from '@tabler/icons-react';
import { useSalvationAdminService, type SalvationStatsView } from '@actschurch/ccms-sdk';
import { LayoutContainer } from '@/components/ui/layout';
import { Alert, Button, Group, Paper, Select, SimpleGrid, Stack, Text, Table, Badge, ScrollArea } from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { FormsStatTile as StatTile, StatTilesSkeleton, SectionCard, InlineEmpty, MeterBar } from '../../FormsManagement/components/FormsUi';
import { useSalvationPermissions } from '@/hooks/salvation/useSalvationPermissions';
import { SalvationPageHeader, StatusBadge, PersonFlags } from '../components/SalvationUi';
import { AddPersonModal } from '../components/PersonModals';
import { useSalvationLookups, useSalvationTeams } from '../components/useSalvationData';
import { formatDateTime, plural, relative, salvationErrorMessage, salvationKeys, salvationPaths } from '../components/salvationUtils';

function progressLabel(s: SalvationStatsView) {
  return `${s.progressPercent}% of ${plural(s.active, 'active person', 'active people')} spoken to or met`;
}

export default function SalvationOverviewPage() {
  const admin = useSalvationAdminService();
  const navigate = useNavigate();
  const perms = useSalvationPermissions();
  const teams = useSalvationTeams();
  const { statuses } = useSalvationLookups();
  const [teamId, setTeamId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: salvationKeys.overview(teamId ? Number(teamId) : null),
    queryFn: () => admin.getOverview(teamId ? Number(teamId) : null),
    refetchInterval: 60_000,
  });

  const t = data?.totals;
  const goPeople = (params: Record<string, string>) => navigate(`${salvationPaths.people}?${new URLSearchParams({ ...(teamId ? { teamId } : {}), ...params })}`);

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          <SalvationPageHeader
            title="Every person matters."
            description="One Lead. One Backup. Clear ownership. A personal welcome."
            actions={
              <>
                {teams.options.length > 1 && (
                  <Select placeholder="Every campus" data={teams.options} value={teamId} onChange={setTeamId} clearable w={220} />
                )}
                {perms.canManagePeople && (
                  <Button leftSection={<IconPlus size={16} />} onClick={() => setAdding(true)}>
                    Add a person
                  </Button>
                )}
              </>
            }
          />

          {error && <Alert color="red">{salvationErrorMessage(error, "The overview couldn't be loaded.")}</Alert>}

          {isLoading || !t ? (
            <StatTilesSkeleton count={5} />
          ) : (
            <>
              <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }}>
                <StatTile icon={<IconUsers size={20} />} color="blue" label="In follow-up" value={t.active} onClick={() => goPeople({ stage: '1' })} />
                <StatTile icon={<IconPhoneOff size={20} />} color="orange" label="Still to reach" value={t.stillToReach} onClick={() => goPeople({ stage: '1', sort: 'due' })} />
                <StatTile icon={<IconPhone size={20} />} color="cyan" label="Spoken to" value={t.spokenTo} onClick={() => goPeople({ statusId: '3' })} />
                <StatTile icon={<IconBuildingChurch size={20} />} color="green" label="Met at church" value={t.met} onClick={() => goPeople({ statusId: '4' })} />
                <StatTile icon={<IconTrendingUp size={20} />} color="grape" label="Handed over" value={t.handedOver} hint={t.connected ? `${t.connected} connected` : undefined} onClick={() => goPeople({ stage: '2' })} />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 2, sm: 4 }}>
                <StatTile icon={<IconClockExclamation size={20} />} color="red" label="Past the deadline" value={t.overdue} emphasis={t.overdue > 0} onClick={() => goPeople({ flag: 'overdue' })} />
                <StatTile icon={<IconArrowsExchange size={20} />} color="orange" label="Backup responsible" value={t.backupResponsible} onClick={() => goPeople({ flag: 'backup' })} />
                <StatTile icon={<IconEye size={20} />} color="grape" label="Needs review" value={t.reviewNeeded} emphasis={t.reviewNeeded > 0} onClick={() => goPeople({ flag: 'review' })} />
                <StatTile icon={<IconUserQuestion size={20} />} color="gray" label="Not assigned" value={t.unassigned} emphasis={t.unassigned > 0} onClick={() => goPeople({ flag: 'unassigned' })} />
              </SimpleGrid>

              <SectionCard icon={<IconHeart size={18} />} color="green" title="Follow-up progress" description={progressLabel(t)}>
                <MeterBar value={t.progressPercent} color="green" height={12} />
                <Text size="xs" c="dimmed" mt={6}>
                  {plural(t.newThisWeek, 'person', 'people')} added this week.
                </Text>
              </SectionCard>

              {t.overdue > 0 && (
                <Alert color="red" title={`${plural(t.overdue, 'person is', 'people are')} past the deadline`}>
                  The Backup is now responsible for each one. Open the list to follow up.
                </Alert>
              )}
            </>
          )}

          <SimpleGrid cols={{ base: 1, lg: 2 }}>
            <SectionCard title="Priorities" description="Nobody has reached them yet. Nearest deadline first." noDivider>
              {!data ? null : data.priorities.length === 0 ? (
                <InlineEmpty icon={<IconHeart size={24} />} title="Everyone in follow-up has been reached." />
              ) : (
                <Stack gap={0}>
                  {data.priorities.map((p) => (
                    <Paper
                      key={p.convertID}
                      p="sm"
                      radius={0}
                      style={{ borderBottom: '1px solid var(--mantine-color-default-border)', cursor: 'pointer' }}
                      onClick={() => navigate(salvationPaths.person(p.convertID))}
                    >
                      <Group justify="space-between" wrap="nowrap" gap="sm">
                        <div style={{ minWidth: 0 }}>
                          <Text fw={600} truncate>{p.fullName}</Text>
                          <Text size="xs" c="dimmed" truncate>
                            {p.responsible ? `${p.responsible.displayName} · ${p.backupIsResponsible ? 'Backup' : 'Lead'} responsible` : 'Not assigned'}
                            {p.dueAt ? ` · due ${relative(p.dueAt)}` : ''}
                          </Text>
                        </div>
                        <Stack gap={4} align="flex-end">
                          <StatusBadge statusId={p.statusID} statuses={statuses} size="xs" />
                          <PersonFlags isOverdue={p.isOverdue} backupIsResponsible={p.backupIsResponsible} isEscalated={p.isEscalated} />
                        </Stack>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              )}
            </SectionCard>

            <SectionCard title="Campus teams" description="Where each campus is this week." noDivider>
              {!data ? null : data.teams.length === 0 ? (
                <InlineEmpty
                  icon={<IconBuildingChurch size={24} />}
                  title="No campus teams yet"
                  description="Set up a team for each campus first."
                  action={<Button size="xs" variant="light" onClick={() => navigate(salvationPaths.teams)}>Campus teams</Button>}
                />
              ) : (
                <ScrollArea>
                  <Table verticalSpacing="sm" miw={420}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Campus team</Table.Th>
                        <Table.Th ta="right">To reach</Table.Th>
                        <Table.Th ta="right">Past deadline</Table.Th>
                        <Table.Th ta="right">Handed over</Table.Th>
                        <Table.Th>Progress</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {data.teams.map((row) => (
                        <Table.Tr key={row.teamID} style={{ cursor: 'pointer' }} onClick={() => navigate(`${salvationPaths.people}?teamId=${row.teamID}`)}>
                          <Table.Td><Text size="sm" fw={600}>{row.teamName}</Text></Table.Td>
                          <Table.Td ta="right">{row.stats.stillToReach}</Table.Td>
                          <Table.Td ta="right">
                            {row.stats.overdue > 0 ? <Badge color="red" variant="light">{row.stats.overdue}</Badge> : 0}
                          </Table.Td>
                          <Table.Td ta="right">{row.stats.handedOver}</Table.Td>
                          <Table.Td w={140}><MeterBar value={row.stats.progressPercent} color="green" tooltip={progressLabel(row.stats)} /></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
              {data && <Text size="xs" c="dimmed" mt="xs">Updated {formatDateTime(new Date().toISOString())}</Text>}
            </SectionCard>
          </SimpleGrid>
        </Stack>
      </FadeIn>

      <AddPersonModal
        opened={adding}
        onClose={() => setAdding(false)}
        defaultTeamId={teamId ? Number(teamId) : null}
        canAssign={perms.canAssign}
        onAdded={(p) => navigate(salvationPaths.person(p.convertID))}
      />
    </LayoutContainer>
  );
}
